import type {
  CompanyManageableRole,
  CompanyMemberDTO,
  CompanyMembersQueryDTO,
  PaginatedCompanyMembersDTO,
} from '@shared/schemas/organization';

export type CompanyMemberOptimisticChange =
  | { readonly type: 'role'; readonly role: CompanyManageableRole }
  | { readonly type: 'status'; readonly active: boolean };

function matchesQuery(member: CompanyMemberDTO, query: CompanyMembersQueryDTO) {
  if (query.role !== 'all' && member.role !== query.role) return false;
  if (query.status === 'active' && member.banned) return false;
  if (query.status === 'disabled' && !member.banned) return false;
  if (query.verification === 'verified' && !member.emailVerified) return false;
  if (query.verification === 'pending' && member.emailVerified) return false;
  if (!query.search) return true;
  const search = query.search.toLocaleLowerCase('fr-FR');
  return member.name.toLocaleLowerCase('fr-FR').includes(search)
    || member.email.toLocaleLowerCase('fr-FR').includes(search);
}

function applyChange(member: CompanyMemberDTO, change: CompanyMemberOptimisticChange) {
  return change.type === 'role'
    ? { ...member, role: change.role }
    : { ...member, banned: !change.active };
}

function sortChangedField(
  items: CompanyMemberDTO[],
  query: CompanyMembersQueryDTO,
  change: CompanyMemberOptimisticChange,
  changedUserId: string,
) {
  if (change.type === 'role' && query.sortBy === 'updatedAt') {
    const changed = items.find((member) => member.id === changedUserId);
    if (!changed) return items;
    const unchanged = items.filter((member) => member.id !== changedUserId);
    return query.sortDirection === 'asc'
      ? [...unchanged, changed]
      : [changed, ...unchanged];
  }

  const sortAffected = change.type === 'role'
    ? query.sortBy === 'role'
    : query.sortBy === 'status';
  if (!sortAffected) return items;

  return [...items].sort((left, right) => {
    const comparison = change.type === 'role'
      ? left.role.localeCompare(right.role)
      : Number(left.banned) - Number(right.banned);
    const directed = query.sortDirection === 'asc' ? comparison : -comparison;
    return directed || left.id.localeCompare(right.id);
  });
}

export function updateCompanyMemberPage(
  previous: PaginatedCompanyMembersDTO,
  query: CompanyMembersQueryDTO,
  userId: string,
  change: CompanyMemberOptimisticChange,
) {
  const current = previous.items.find((member) => member.id === userId);
  if (!current) return previous;

  const updated = applyChange(current, change);
  if (!matchesQuery(updated, query)) {
    const total = Math.max(0, previous.meta.total - 1);
    return {
      items: previous.items.filter((member) => member.id !== userId),
      meta: {
        ...previous.meta,
        pageCount: Math.max(1, Math.ceil(total / previous.meta.pageSize)),
        total,
      },
    };
  }

  const items = previous.items.map((member) => member.id === userId ? updated : member);
  return { ...previous, items: sortChangedField(items, query, change, userId) };
}
